import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useSession } from '../../session';

type Status = 'pending' | 'approved' | 'rejected' | 'cancelled';
type EnrollmentRequest = {
  id: number; userId: number; courseId: number; courseName: string; courseStatus: string;
  firstName: string; lastName: string; login: string; userStatus: string;
  status: Status; revision: number; reason: string | null; requestedAt: string; reviewedAt: string | null;
};
type Queue = { requests: EnrollmentRequest[]; total: number; page: number; pageSize: number; scope: 'all' | 'assigned' };

export function EnrollmentRequests() {
  const { user } = useSession();
  const can = (key: string) => (user?.permissions ?? []).some(permission => permission === key || key.startsWith(permission + '.'));
  const canReadPeople = can('administrator.users.read');
  const canReadCourses = can('administrator.courses.read');
  const [status, setStatus] = useState<Status | 'all'>('pending');
  const [page, setPage] = useState(1);
  const [queue, setQueue] = useState<Queue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const load = useCallback(async () => {
    const result = await api.get<Queue>(`/admin/enrollment-requests?status=${status}&page=${page}`);
    setQueue(result);
  }, [status, page]);
  useEffect(() => { setQueue(null); setError(null); void load().catch(e => setError((e as Error).message)); }, [load]);

  async function decide(row: EnrollmentRequest, decision: 'approved' | 'rejected') {
    const reason = (reasons[row.id] ?? '').trim();
    if (decision === 'rejected' && !reason) { setError('Give the learner a reason for rejecting their request.'); return; }
    setBusy(row.id); setError(null); setNotice(null);
    try {
      await api.post(`/admin/enrollment-requests/${row.id}/decision`, { decision, reason, revision: row.revision });
      setNotice(`${row.firstName}'s request for ${row.courseName} was ${decision}.`);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  return <>
    <h1 className="h1">Enrollment requests</h1>
    <p className="muted">Approve requested training or explain why it cannot be approved. Places, prerequisites and active accounts are checked again when you approve.</p>
    {error && <p className="auth__error" role="alert">{error}</p>}
    {notice && <p className="notice" role="status">{notice}</p>}
    <label>Status <select value={status} onChange={e => { setStatus(e.target.value as Status | 'all'); setPage(1); }}>
      <option value="pending">Awaiting approval</option><option value="approved">Approved</option>
      <option value="rejected">Rejected</option><option value="cancelled">Cancelled</option><option value="all">All requests</option>
    </select></label>
    {!queue && !error && <p className="muted">Loading…</p>}
    {queue && <>
      <p className="muted small">{queue.total} {queue.total === 1 ? 'request' : 'requests'}</p>
      <ul className="cardlist">{queue.requests.map(row => <li className="cardlist__item" key={row.id}>
        <div className="cardlist__main">
          <strong>{canReadPeople ? <Link to={`/admin/people/${row.userId}`}>{row.firstName} {row.lastName}</Link> : <>{row.firstName} {row.lastName}</>} · {row.login}</strong>
          {canReadCourses ? <Link to={`/admin/courses/${row.courseId}`}>{row.courseName}</Link> : <span>{row.courseName}</span>}
          <span className="muted small">Requested {new Date(row.requestedAt).toLocaleString()} · {row.status}</span>
          {(row.userStatus !== 'active' || row.courseStatus !== 'active') && <span className="warn small">{row.userStatus !== 'active' ? 'Learner inactive. ' : ''}{row.courseStatus !== 'active' ? 'Course inactive.' : ''}</span>}
          {row.reason && <p>{row.reason}</p>}
          {row.status === 'pending' && <>
            <label>Reason (required when rejecting)<textarea maxLength={2000} value={reasons[row.id] ?? ''}
              onChange={e => setReasons(current => ({ ...current, [row.id]: e.target.value }))} /></label>
            <div className="toolbar">
              <button className="btn btn--primary" disabled={busy !== null || row.userStatus !== 'active' || row.courseStatus !== 'active'} onClick={() => void decide(row, 'approved')}>{busy === row.id ? 'Saving…' : 'Approve enrollment'}</button>
              <button className="btn btn--ghost" disabled={busy !== null || !(reasons[row.id] ?? '').trim()} onClick={() => void decide(row, 'rejected')}>Reject request</button>
            </div>
          </>}
        </div>
      </li>)}</ul>
      {queue.requests.length === 0 && <p className="notice">No requests in this view.</p>}
      <nav className="toolbar" aria-label="Request pages">
        <button className="btn btn--ghost" disabled={page === 1 || busy !== null} onClick={() => setPage(page - 1)}>Previous</button>
        <span>Page {page}</span><button className="btn btn--ghost" disabled={page * queue.pageSize >= queue.total || busy !== null} onClick={() => setPage(page + 1)}>Next</button>
      </nav>
    </>}
  </>;
}
